/**
 * Trunnion AI - Session Info Settings
 * Shows current user, role, clearance, and token info.
 */

import { Shield, User, Clock, Key } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ClassificationTag } from '@/components/ui/ClassificationTag';

export function SessionInfo() {
  const { user, clearanceContext } = useAuthStore();

  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Current Session</h3>
          <div className="space-y-4">
            <InfoRow
              icon={<User className="h-4 w-4 text-trunnion-slate-400" />}
              label="Operator"
              value={user?.displayName ?? 'Unknown'}
            />
            <InfoRow
              icon={<Key className="h-4 w-4 text-trunnion-slate-400" />}
              label="Username"
              value={user?.username ?? 'Unknown'}
            />
            <InfoRow
              icon={<Shield className="h-4 w-4 text-trunnion-slate-400" />}
              label="Role"
              value={
                <Badge variant="info">{user?.role ?? 'Unknown'}</Badge>
              }
            />
            <InfoRow
              icon={<Shield className="h-4 w-4 text-trunnion-slate-400" />}
              label="Clearance Level"
              value={
                <ClassificationTag level={clearanceContext?.operatorClearance ?? 'UNCLASSIFIED'} />
              }
            />
            <InfoRow
              icon={<Shield className="h-4 w-4 text-trunnion-slate-400" />}
              label="Session Classification"
              value={
                <ClassificationTag level={clearanceContext?.sessionClassification ?? 'UNCLASSIFIED'} />
              }
            />
            {clearanceContext?.compartments && clearanceContext.compartments.length > 0 && (
              <InfoRow
                icon={<Shield className="h-4 w-4 text-trunnion-slate-400" />}
                label="Compartments"
                value={
                  <div className="flex flex-wrap gap-1">
                    {clearanceContext.compartments.map((c) => (
                      <Badge key={c} variant="default">{c}</Badge>
                    ))}
                  </div>
                }
              />
            )}
            {clearanceContext?.validUntil && (
              <InfoRow
                icon={<Clock className="h-4 w-4 text-trunnion-slate-400" />}
                label="Session Expires"
                value={new Date(clearanceContext.validUntil).toLocaleString()}
              />
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      {icon}
      <span className="text-sm text-trunnion-slate-400 w-40">{label}</span>
      <div className="text-sm text-white">{value}</div>
    </div>
  );
}
